import { Camera } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import imagePlaceholder from "@/assets/images/image.png";
import { getImageUrl } from "@/lib/utils";

interface ProfileAvatarProps {
  avatarPreview: string;
  isEditing: boolean;
  localAvailability: "available" | "offline" | "enterprise";
  handleAvatarClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onAvatarChange?: (previewUrl: string, file: File) => void;
}

export const ProfileAvatar = ({
  avatarPreview,
  isEditing,
  localAvailability,
  handleAvatarClick,
  fileInputRef,
  onAvatarChange,
}: ProfileAvatarProps) => {

  const getAvailabilityColor = () => {
    switch (localAvailability) {
      case "available":
        return "bg-emerald-100 text-emerald-800 hover:bg-emerald-200";
      case "offline":
        return "bg-gray-100 text-gray-800 hover:bg-gray-200";
      default:
        return "bg-emerald-100 text-emerald-800 hover:bg-emerald-200";
    }
  };

  const getAvailabilityText = () => {
    switch (localAvailability) {
      case "available":
        return "Available";
      case "offline":
        return "Not Available";
      default:
        return "Available for Work";
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      onAvatarChange && onAvatarChange(previewUrl, file);
      e.target.value = "";
    }
  };


  return (
    <div className="relative w-32 h-32 mx-auto mb-4" onClick={handleAvatarClick}>
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-200 to-emerald-200 opacity-50"></div>
      <img
        src={getImageUrl(avatarPreview) || imagePlaceholder}
        alt="Profile"
        width={144}
        height={144}
        loading="eager"
        className={`w-full h-full rounded-full bg-white p-1 shadow-md relative z-10 ${isEditing ? "cursor-pointer hover:opacity-90" : ""
          }`}
      />
      {isEditing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded-full opacity-0 hover:opacity-100 transition-opacity z-20">
          <div className="bg-white p-2 rounded-full">
            <Camera className="w-8 h-8 text-gray-700" />
          </div>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      {localAvailability !== "enterprise" && (
        <Badge
          className={`absolute bottom-1 right-1 ${getAvailabilityColor()} transition-colors cursor-pointer z-20 shadow-sm`}
        >
          {getAvailabilityText()}
        </Badge>
      )}
    </div>
  );
};
